import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { api } from '../services/api';

interface WorkflowExecution {
  id: string;
  name: string;
  totalExecutions: number;
  successfulExecutions: number;
  runningExecutions: number;
  averageDuration: number;
  lastExecution?: string;
  isActive: boolean;
}

interface ExecutionsData {
  workflows: WorkflowExecution[];
  totalWorkflows: number;
  totalExecutions: number;
  totalSuccessful: number;
  totalRunning: number;
  executionLimit: number;
}

const CACHE_KEY = 'executions_default_stats_cache';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 horas

function loadCache(): ExecutionsData | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.timestamp || !parsed.data) return null;
    const age = Date.now() - parsed.timestamp;
    if (age > CACHE_TTL_MS) return null;
    return parsed.data as ExecutionsData;
  } catch {
    return null;
  }
}

function saveCache(data: ExecutionsData) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), data }));
  } catch {}
}

const Executions: React.FC = () => {
  const [data, setData] = useState<ExecutionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const theme = useTheme();

  const fetchData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Tentar cache (24h)
      const cached = loadCache();
      if (cached) {
        setData(cached);
        setLoading(false);
        return;
      }

      console.log('🔄 Iniciando busca de dados de execução...');
      
      // Configurar timeout de 5 minutos para a requisição
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5 * 60 * 1000); // 5 minutos
      
      const response = await api.get('/n8n/default/stats?days=30', {
        signal: controller.signal,
        timeout: 5 * 60 * 1000 // 5 minutos
      });
      
      clearTimeout(timeoutId);
      
      if (response.data.success) {
        const stats = response.data.data.stats || {};
        
        // Garantir que todos os valores sejam números válidos
        const totalExecutions = parseInt(stats.totalExecutions) || 0;
        const successfulExecutions = parseInt(stats.successfulExecutions) || 0;
        const runningExecutions = parseInt(stats.runningExecutions) || 0;
        const averageDuration = parseFloat(stats.averageDuration) || 0;
        
        // Criar array com apenas o workflow padrão
        const workflowsArray: WorkflowExecution[] = [
          {
            id: 'pPbbrcOrhUHu8LQ9',
            name: 'Workflow Principal',
            totalExecutions,
            successfulExecutions,
            runningExecutions,
            averageDuration,
            lastExecution: stats.lastExecution?.startedAt,
            isActive: true,
          }
        ];

        const executionLimit = parseInt(process.env.REACT_APP_EXECUTION_LIMIT || '25000');
        
        const built: ExecutionsData = {
          workflows: workflowsArray,
          totalWorkflows: 1,
          totalExecutions,
          totalSuccessful: successfulExecutions,
          totalRunning: runningExecutions,
          executionLimit,
        };
        setData(built);
        saveCache(built); // Persistir por 24h
        
        console.log('✅ Dados de execução carregados com sucesso');
      } else {
        setError('Erro ao carregar dados de execução');
      }
    } catch (err: any) {
      console.error('Erro ao buscar dados de execução:', err);
      
      if (err.name === 'AbortError' || err.code === 'ECONNABORTED') {
        setError('A requisição foi cancelada devido ao tempo limite. O sistema está processando muitos dados. Tente novamente em alguns minutos.');
      } else if (err.response?.status === 500) {
        setError('Erro interno do servidor. O sistema pode estar sobrecarregado. Tente novamente em alguns minutos.');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Erro ao carregar dados de execução. Verifique sua conexão e tente novamente.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    // Forçar atualização ignorando cache: limpar e buscar novamente
    localStorage.removeItem(CACHE_KEY);
    setRefreshing(true);
    fetchData();
  };

  const getUsagePercentage = (total: number, limit: number) => {
    return Math.min((total / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Box p={3}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          textAlign="center"
        >
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" component="h2" gutterBottom>
            Carregando dados de execução...
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 500, mb: 2 }}>
            Estamos buscando todas as execuções dos últimos 30 dias. 
            Este processo pode levar alguns minutos devido ao grande volume de dados.
          </Typography>
          <Alert severity="info" sx={{ maxWidth: 500 }}>
            <Typography variant="body2">
              <strong>Dica:</strong> Você pode continuar usando outras funcionalidades da plataforma 
              e voltar em alguns instantes. Os dados estarão disponíveis assim que o processamento for concluído.
            </Typography>
          </Alert>
          <Alert severity="success" sx={{ maxWidth: 500, mt: 2 }}>
            <Typography variant="body2">
              <strong>Sistema de Banco de Dados:</strong> Os dados são armazenados no Supabase por 24 horas para melhor performance. 
              A primeira busca pode demorar alguns minutos, mas as próximas serão instantâneas.
            </Typography>
          </Alert>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <IconButton
            color="inherit"
            size="small"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshIcon />
          </IconButton>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box p={3}>
        <Alert severity="info">
          Nenhum dado de execução disponível
        </Alert>
      </Box>
    );
  }

  const usagePercentage = getUsagePercentage(data.totalExecutions, data.executionLimit);
  const usageColor = getUsageColor(usagePercentage);

  return (
    <Box p={3}>
      {/* Header */}
      <Box className="sophisticated-card" sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        p: 3,
        background: 'var(--glass-bg)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--border-radius-medium)',
        boxShadow: 'var(--shadow-medium)',
      }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Execuções
        </Typography>
        <Tooltip title="Atualizar dados">
          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            color="primary"
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Resumo Geral */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card className="sophisticated-card" sx={{
            background: 'var(--gradient-primary)',
            color: '#000000',
            boxShadow: 'var(--shadow-colored)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 30px rgba(25, 118, 210, 0.25)',
            },
          }}>
            <CardContent>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }} gutterBottom>
                Total de Execuções
              </Typography>
              <Typography variant="h4" component="div" sx={{ color: '#000000' }}>
                {(data.totalExecutions || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Limite: {(data.executionLimit || 10000).toLocaleString()}
              </Typography>
              <Box mt={1}>
                <LinearProgress
                  variant="determinate"
                  value={usagePercentage}
                  color={usageColor as any}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="textSecondary">
                  {usagePercentage.toFixed(1)}% do limite utilizado
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Removidos cards de Taxa de Sucesso e Falhas */}

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Em Execução
              </Typography>
              <Typography variant="h4" component="div" color="warning.main">
                {(data.totalRunning || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Execuções ativas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela de Workflows */}
      <Paper elevation={2}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Workflows
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Workflow</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Total</TableCell>
                <TableCell align="center">Sucessos</TableCell>
                {/* <TableCell align="center">Falhas</TableCell> */}
                <TableCell align="center">Em Execução</TableCell>
                {/* <TableCell align="center">Taxa de Sucesso</TableCell> */}
                <TableCell align="center">Duração Média</TableCell>
                <TableCell align="center">Última Execução</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.workflows.map((workflow) => (
                <TableRow key={workflow.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1" fontWeight="medium">
                        {workflow.name}
                      </Typography>
                      {workflow.isActive && (
                        <Chip
                          label="Ativo"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={workflow.isActive ? 'Ativo' : 'Inativo'}
                      size="small"
                      color={workflow.isActive ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="medium">
                      {(workflow.totalExecutions || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Typography variant="body2">
                        {(workflow.successfulExecutions || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </TableCell>
                  {/* Removidas colunas de Falhas e Taxa de Sucesso */}
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      <ScheduleIcon color="warning" fontSize="small" />
                      <Typography variant="body2">
                        {(workflow.runningExecutions || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {workflow.averageDuration.toFixed(1)}s
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="textSecondary">
                      {workflow.lastExecution ? 
                        new Date(workflow.lastExecution).toLocaleString('pt-BR') : 
                        'N/A'
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Informações Adicionais */}
      <Box mt={3}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Período analisado:</strong> Últimos 30 dias
          </Typography>
                       <Typography variant="body2">
               <strong>Limite de execuções:</strong> {(data.executionLimit || 10000).toLocaleString()} por mês
             </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

export default Executions; 